import AddProjectCostModal from '@/components/features/Projects/components/AddProjectCostModal';
import ProjectDetailsPanel from '@/components/features/Projects/components/ProjectDetailsPanel';
import ProjectHistoryTab from '@/components/features/Projects/components/ProjectHistoryTab';
import DeleteProjectCostModal from '@/components/features/Projects/forms/DeleteProjectCostModal';
import EditProjectCostModal from '@/components/features/Projects/forms/EditProjectCostModal';
import GanttChart from '@/components/features/Projects/GanttChart';
import EditProjectModal from '@/components/features/Projects/modals/EditProjectModal';
import ProjectManufacturingNumberModal from '@/components/features/Projects/modals/ProjectManufacturingNumberModal';
import TaskKanbanBoard from '@/components/features/Projects/TaskKanbanBoard';
import LinkedPurchaseOrdersSection from '@/components/features/shared/costs/LinkedPurchaseOrdersSection';
import UniversalCostsComponent from '@/components/features/shared/costs/UniversalCostsComponent';
import { EntityWithCosts } from '@/components/features/shared/costs/utils';
import { DeleteActionButton, DeleteConfirmAlert, DETAIL_ACTION_BUTTON_CLASS } from '@/components/global/detail-page-actions';
import type { PersonOption } from '@/components/global/PersonSelect';
import { ResponsiveActions, type ResponsiveActionItem } from '@/components/global/ResponsiveActions';
import type { CalculationRequestOption } from '@/components/global/selectors/CalculationRequestSelect';
import { SheetPickerList } from '@/components/global/SheetPickerList';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/shadcn/drawer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu';
import { Label } from '@/components/ui/shadcn/label';
import { MultiSelect } from '@/components/ui/shadcn/multi-select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useSettings } from '@/contexts/SettingsContext';
import { useTranslations } from '@/hooks/useTranslations';
import ShowLayout from '@/layouts/show-layout';
import { laravelJsonFetch } from '@/lib/laravelJsonFetch';
import { settingLabel } from '@/lib/status-labels';
import { cn, useCan } from '@/lib/utils';
import { BreadcrumbItem, PageProps, Project, ProjectCostLine, User } from '@/types';
import { Head, router } from '@inertiajs/react';
import {
    ChartGantt,
    Columns3,
    Edit,
    Euro,
    FileSpreadsheet,
    Hash,
    History,
    Info,
    MoreHorizontal,
    Plus,
    RefreshCw,
    SignpostBig,
    Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

interface Props extends PageProps {
    project: Project;
    users: User[];
    canRevertTasks?: boolean;
    /** The calculation request this project was priced from. */
    linkedCalculationRequest?: CalculationRequestOption | null;
    /** The pick list — deferred, so it is absent on first paint. */
    calculationRequestOptions?: CalculationRequestOption[];
    canLinkCalculationRequest?: boolean;
    /** Contacts registered against this project's company — the contact picker's list. */
    contactCandidates?: PersonOption[];
    /** Everyone active, with the commercially-responsible roles surfaced first. */
    ownerCandidates?: { people: PersonOption[]; suggested_ids: string[] };
    /** Active colleagues holding the Project Manager role — the project manager picker's list. */
    projectManagerCandidates?: PersonOption[];
    /** Active colleagues holding the Engineer role — the engineer picker's list. */
    projectEngineerCandidates?: PersonOption[];
    /** Whether this viewer may staff the project — manager and engineer: owner, creator or administrator. */
    canAssignProjectStaff?: boolean;
}

export default function Show({
    project,
    users,
    canRevertTasks = false,
    linkedCalculationRequest = null,
    calculationRequestOptions = [],
    canLinkCalculationRequest = false,
    contactCandidates = [],
    ownerCandidates = { people: [], suggested_ids: [] },
    projectManagerCandidates = [],
    projectEngineerCandidates = [],
    canAssignProjectStaff = false,
}: Props) {
    const { t } = useTranslations();
    const { can } = useCan();
    const { settings } = useSettings();
    const [selectedCost, setSelectedCost] = useState<ProjectCostLine | null>(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('gantt');
    const [mobileActionsDrawerOpen, setMobileActionsDrawerOpen] = useState(false);
    const [templateModalOpen, setTemplateModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [editProjectOpen, setEditProjectOpen] = useState(false);
    const [manufacturingNumberOpen, setManufacturingNumberOpen] = useState(false);
    const [addCostOpen, setAddCostOpen] = useState(false);
    const [exportGanttOpen, setExportGanttOpen] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [statusNote, setStatusNote] = useState('');

    /**
     * Closes the actions drawer, then runs the opener once it has animated out.
     * The delay is not cosmetic: mounting the next overlay while this one is
     * still leaving stacks two of them and leaves the body with a stuck
     * pointer-events lock. It cannot hang off the drawer's onOpenChange — vaul
     * only fires that for closes it initiates itself.
     */
    const requestActionFromDrawer = (open: () => void) => {
        setMobileActionsDrawerOpen(false);

        window.setTimeout(open, 350);
    };
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [sortByDepartment, setSortByDepartment] = useState(false);
    const [isTabletOrMobile, setIsTabletOrMobile] = useState(false);
    const [taskView, setTaskViewState] = useState<'gantt' | 'kanban'>('gantt');
    // Once the user picks a view we stop auto-switching it on viewport changes.
    const taskViewPickedRef = useRef(false);
    const setTaskView = (view: 'gantt' | 'kanban') => {
        taskViewPickedRef.current = true;
        setTaskViewState(view);
    };

    const currentYear = new Date().getFullYear().toString();
    const [exportTaskStatuses, setExportTaskStatuses] = useState<string[]>(() => settings.projectTaskStatuses.map((s) => s.value));
    const [exportTimelineYears, setExportTimelineYears] = useState<string[]>([currentYear]);
    const [exportIncludeAllTimelineRows, setExportIncludeAllTimelineRows] = useState(false);

    const appliedTemplateKey = useMemo(() => {
        const templateRootTask = project.tasks.find((task) => task.is_template_root || Boolean(task.template_key));
        if (!templateRootTask || typeof templateRootTask.template_key !== 'string') {
            return '';
        }

        return templateRootTask.template_key.trim();
    }, [project.tasks]);

    const templateOptions = useMemo(() => {
        const options = (settings.projectTaskTemplates ?? [])
            .map((template) => {
                if (!template || typeof template !== 'object') {
                    return null;
                }
                const record = template as Record<string, unknown>;
                const value =
                    typeof record.value === 'string'
                        ? record.value.trim()
                        : typeof record.key === 'string'
                          ? record.key.trim()
                          : typeof record.name === 'string'
                            ? record.name.trim()
                            : '';
                if (value.length === 0) {
                    return null;
                }
                const label =
                    typeof record.label === 'string' && record.label.trim().length > 0
                        ? record.label
                        : typeof record.name === 'string' && record.name.trim().length > 0
                          ? record.name
                          : value;
                return { value, label };
            })
            .filter((template): template is { value: string; label: string } => Boolean(template));

        if (appliedTemplateKey && !options.some((option) => option.value === appliedTemplateKey)) {
            return [
                {
                    value: appliedTemplateKey,
                    label: appliedTemplateKey,
                },
                ...options,
            ];
        }

        return options;
    }, [appliedTemplateKey, settings.projectTaskTemplates]);

    const projectHasTemplateTasks = useMemo(() => project.tasks.some((task) => task.is_template_root || Boolean(task.template_key)), [project.tasks]);

    const projectStatusOptions = useMemo(
        () =>
            settings.projectStatuses.map((status) => ({
                id: status.value,
                label: settingLabel(t, 'project_statuses', status.value) || status.label,
                badge:
                    status.value === String(project.status ?? '') ? (
                        <Badge variant="secondary">{t('projects.show.change_status.current', 'Current')}</Badge>
                    ) : null,
            })),
        [settings.projectStatuses, project.status, t],
    );
    const canUpdateManufacturingNumber = can('projects.manufcaturing_number');
    const canDeleteProject = can('projects.delete');
    const canUpdateProject = can('projects.update');
    // Separate from projects.update: editing a project's details does not carry the
    // right to declare it completed, on hold or cancelled.
    const canChangeStatus = can('projects.status.change');
    const canViewCosts = can('costs.projects.view');
    const canManageCosts = can('costs.projects.manage');

    useEffect(() => {
        setSelectedTemplate((currentValue) => {
            if (currentValue && templateOptions.some((option) => option.value === currentValue)) {
                return currentValue;
            }

            if (appliedTemplateKey && templateOptions.some((option) => option.value === appliedTemplateKey)) {
                return appliedTemplateKey;
            }

            return templateOptions[0]?.value ?? '';
        });
    }, [appliedTemplateKey, templateOptions]);

    const taskStatusExportOptions = useMemo(
        () => settings.projectTaskStatuses.map((s) => ({ value: s.value, label: settingLabel(t, 'project_task_statuses', s.value) })),
        [settings.projectTaskStatuses, t],
    );

    const exportTimelineYearOptions = useMemo(() => {
        const years = new Set<number>();
        years.add(new Date().getFullYear());
        if (project.start_date) {
            years.add(new Date(project.start_date).getFullYear());
        }
        if (project.end_date) {
            years.add(new Date(project.end_date).getFullYear());
        }
        return Array.from(years)
            .sort((a, b) => b - a)
            .map((y) => ({ value: String(y), label: String(y) }));
    }, [project.start_date, project.end_date]);

    const projectPageHeading = useMemo(() => {
        const name = project.contract_name?.trim() || '';
        const number = project.contract_number?.trim();
        if (number && name) {
            return `${number} - ${name}`;
        }
        if (name) {
            return name;
        }
        if (number) {
            return number;
        }

        return String(project.id);
    }, [project.contract_name, project.contract_number, project.id]);

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: t('projects.index.breadcrumbs.projects', 'Projects'),
            href: '/projects',
        },
        {
            title: projectPageHeading,
            href: `/projects/${project.id}`,
        },
    ];

    const tabs = useMemo(
        () =>
            [
                { value: 'gantt', label: t('projects.show.tabs.gantt', 'Gantt') },
                { value: 'details', label: t('projects.show.tabs.details', 'Details') },
                canViewCosts ? { value: 'costs', label: t('projects.show.tabs.costs', 'Costs') } : null,
                // One timeline for the whole project: task edits and project-level
                // changes, merged server-side. These were two tabs, so "what
                // happened here" had to be asked twice and merged by eye.
                { value: 'history', label: t('projects.show.tabs.history', 'History') },
            ].filter((tab): tab is { value: string; label: string } => tab !== null),
        [t, canViewCosts],
    );

    const defaultTab = useMemo(() => {
        if (tabs.some((tab) => tab.value === 'gantt')) {
            return 'gantt';
        }

        return tabs[0]?.value ?? 'gantt';
    }, [tabs]);

    useEffect(() => {
        setActiveTab(defaultTab);
    }, [defaultTab]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const mediaQuery = window.matchMedia('(max-width: 1023px)');
        const syncViewport = () => {
            setIsTabletOrMobile(mediaQuery.matches);
        };

        syncViewport();
        mediaQuery.addEventListener('change', syncViewport);
        window.addEventListener('resize', syncViewport);

        return () => {
            mediaQuery.removeEventListener('change', syncViewport);
            window.removeEventListener('resize', syncViewport);
        };
    }, []);

    // Default the task tab to Kanban on mobile/tablet (the Gantt does not fit
    // small screens) and to Gantt on desktop, until the user picks a view.
    useEffect(() => {
        if (taskViewPickedRef.current) {
            return;
        }
        setTaskViewState(isTabletOrMobile ? 'kanban' : 'gantt');
    }, [isTabletOrMobile]);

    const desktopMaxVisibleTabs = 5;
    const visibleTabs = isTabletOrMobile ? tabs : tabs.slice(0, desktopMaxVisibleTabs);
    const overflowTabs: typeof tabs = isTabletOrMobile ? [] : tabs.slice(desktopMaxVisibleTabs);
    const hasOverflow = overflowTabs.length > 0;
    const tabIcons: Record<string, ReactNode> = {
        gantt: <ChartGantt className="h-4 w-4" />,
        details: <Info className="h-4 w-4" />,
        costs: <Euro className="h-4 w-4" />,
        history: <History className="h-4 w-4" />,
    };

    const openExportGanttDialog = () => {
        setExportTaskStatuses(settings.projectTaskStatuses.map((s) => s.value));
        const yearsFromProject = new Set<string>();
        if (project.start_date != null) {
            yearsFromProject.add(String(new Date(project.start_date).getFullYear()));
        }
        if (project.end_date != null) {
            yearsFromProject.add(String(new Date(project.end_date).getFullYear()));
        }
        setExportTimelineYears(yearsFromProject.size > 0 ? Array.from(yearsFromProject) : [currentYear]);
        setExportIncludeAllTimelineRows(false);
        setExportGanttOpen(true);
    };

    const buildExportGanttParams = () => {
        const params = new URLSearchParams();
        exportTaskStatuses.forEach((s) => params.append('task_statuses[]', s));
        exportTimelineYears.forEach((y) => params.append('timeline_year[]', y));
        if (exportIncludeAllTimelineRows) {
            params.append('include_all_timeline_rows', '1');
        }
        return params;
    };

    const handleExportGanttDownload = () => {
        window.open(`${route('projects.gantt-export', project.id)}?${buildExportGanttParams().toString()}`, '_blank', 'noopener,noreferrer');
        setExportGanttOpen(false);
    };

    const openStatusModal = () => {
        setSelectedStatus(String(project.status ?? ''));
        setStatusNote('');
        setStatusModalOpen(true);
    };

    const handleApplyStatus = () => {
        if (!selectedStatus || selectedStatus === String(project.status ?? '')) {
            setStatusModalOpen(false);
            return;
        }

        router.patch(
            route('projects.update-status', project.id),
            { status: selectedStatus, status_change_note: statusNote.trim() || undefined },
            {
                preserveScroll: true,
                // The note only exists on the timeline, so pull it back in with the project.
                onSuccess: () => {
                    setStatusModalOpen(false);
                    router.reload({ only: ['project'] });
                },
                onError: (errors) => toast.error(errors.status ?? t('projects.flash.no_permission_update', 'You cannot update this project.')),
            },
        );
    };

    const openTemplateModal = () => {
        const defaultTemplate =
            (appliedTemplateKey && templateOptions.some((option) => option.value === appliedTemplateKey) && appliedTemplateKey) ||
            (selectedTemplate && templateOptions.some((option) => option.value === selectedTemplate) && selectedTemplate) ||
            templateOptions[0]?.value ||
            '';
        setSelectedTemplate(defaultTemplate);
        setTemplateModalOpen(true);
    };

    const handleApplyTemplate = () => {
        if (!selectedTemplate) {
            return;
        }

        if (projectHasTemplateTasks) {
            const shouldContinue = window.confirm(
                t(
                    'projects.show.change_template.overwrite_alert',
                    'This project already has template-generated tasks. Applying a template again will overwrite all current template task changes. Continue?',
                ),
            );

            if (!shouldContinue) {
                return;
            }
        }

        router.post(route('projects.apply-template', project.id), { project_task_template: selectedTemplate }, { preserveScroll: true });
        setTemplateModalOpen(false);
    };

    const departmentLabelByKey = useMemo(() => {
        const map = new Map<string, string>();
        (settings.projectTaskDepartments ?? []).forEach((department) => {
            if (department?.value && department?.label) {
                map.set(String(department.value), String(department.label));
            }
        });
        (settings.availableRoles ?? []).forEach((roleName) => {
            map.set(`role:${roleName}`, roleName);
        });
        return map;
    }, [settings.projectTaskDepartments, settings.availableRoles]);

    const ganttTasks = useMemo(() => {
        if (!sortByDepartment) {
            return project.tasks;
        }

        const taskById = new Map(project.tasks.map((task) => [task.id, task]));
        const childrenByParentId = new Map<string, typeof project.tasks>();
        const rootTasks: typeof project.tasks = [];

        project.tasks.forEach((task) => {
            const parentId = task.parent_task_id ? String(task.parent_task_id) : '';
            if (!parentId || !taskById.has(parentId)) {
                rootTasks.push(task);
                return;
            }

            const existingChildren = childrenByParentId.get(parentId) ?? [];
            existingChildren.push(task);
            childrenByParentId.set(parentId, existingChildren);
        });

        const resolveDepartmentLabel = (task: (typeof project.tasks)[number]): string => {
            const key = String(task.department_key ?? '').trim();
            if (key.length === 0) {
                return '';
            }
            return departmentLabelByKey.get(key) ?? key;
        };

        const sortedRoots = [...rootTasks].sort((a, b) => {
            if (a.is_project_timeline && !b.is_project_timeline) {
                return -1;
            }
            if (!a.is_project_timeline && b.is_project_timeline) {
                return 1;
            }

            return resolveDepartmentLabel(a).localeCompare(resolveDepartmentLabel(b), undefined, { sensitivity: 'base' });
        });

        const flattened: typeof project.tasks = [];
        const appendSubtree = (task: (typeof project.tasks)[number]) => {
            flattened.push(task);
            const children = childrenByParentId.get(String(task.id)) ?? [];
            children.forEach((child) => appendSubtree(child));
        };

        sortedRoots.forEach((rootTask) => appendSubtree(rootTask));

        return flattened;
    }, [project, sortByDepartment, departmentLabelByKey]);

    const sortedTopLevelReorderPayload = useMemo(() => {
        const taskById = new Map(project.tasks.map((task) => [task.id, task]));
        const rootTasks = project.tasks.filter((task) => {
            const parentId = task.parent_task_id ? String(task.parent_task_id) : '';
            return !parentId || !taskById.has(parentId);
        });

        const resolveDepartmentLabel = (task: (typeof project.tasks)[number]): string => {
            const key = String(task.department_key ?? '').trim();
            if (key.length === 0) {
                return '';
            }
            return departmentLabelByKey.get(key) ?? key;
        };

        const sortedRoots = [...rootTasks].sort((a, b) => {
            if (a.is_project_timeline && !b.is_project_timeline) {
                return -1;
            }
            if (!a.is_project_timeline && b.is_project_timeline) {
                return 1;
            }

            return resolveDepartmentLabel(a).localeCompare(resolveDepartmentLabel(b), undefined, { sensitivity: 'base' });
        });

        return {
            tasks: sortedRoots.map((task, index) => ({
                id: task.id,
                sort_order: index,
            })),
        };
    }, [project, departmentLabelByKey]);

    const handleSortByDepartment = async () => {
        if (sortedTopLevelReorderPayload.tasks.length === 0) {
            return;
        }

        try {
            await laravelJsonFetch(route('projects.tasks.reorder', project.id), 'POST', sortedTopLevelReorderPayload);
            setSortByDepartment(true);
            toast.success(t('projects.show.messages.tasks_sorted_by_department', 'Tasks sorted by department'));
            router.reload({ only: ['project'] });
        } catch {
            toast.error(t('projects.show.messages.failed_sort_tasks_by_department', 'Failed to sort tasks by department'));
        }
    };

    const actionItems: ResponsiveActionItem[] = [
        ...(canUpdateManufacturingNumber
            ? [
                  {
                      key: 'manufacturing-number',
                      label: t('projects.show.manufacturing_number.button', 'Manufacturing number'),
                      icon: <Hash className="h-4 w-4" />,
                      onOverflowSelect: () => setManufacturingNumberOpen(true),
                      node: (
                          <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className={DETAIL_ACTION_BUTTON_CLASS}
                              onClick={() => setManufacturingNumberOpen(true)}
                          >
                              <Hash className="h-4 w-4" />
                              {t('projects.show.manufacturing_number.button', 'Manufacturing number')}
                          </Button>
                      ),
                  } satisfies ResponsiveActionItem,
              ]
            : []),
        ...(canManageCosts
            ? [
                  {
                      key: 'add-cost',
                      label: t('projects.sidebar.add_cost', 'Add Cost'),
                      icon: <Plus className="h-4 w-4" />,
                      onOverflowSelect: () => setAddCostOpen(true),
                      node: (
                          <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className={DETAIL_ACTION_BUTTON_CLASS}
                              onClick={() => setAddCostOpen(true)}
                          >
                              <Plus className="h-4 w-4" />
                              {t('projects.sidebar.add_cost', 'Add Cost')}
                          </Button>
                      ),
                  } satisfies ResponsiveActionItem,
              ]
            : []),
        {
            key: 'download',
            label: t('projects.index.export_gantt.download', 'Download'),
            icon: <FileSpreadsheet className="h-4 w-4" />,
            onOverflowSelect: openExportGanttDialog,
            node: (
                <Button type="button" variant="outline" size="sm" className={DETAIL_ACTION_BUTTON_CLASS} onClick={openExportGanttDialog}>
                    <FileSpreadsheet className="h-4 w-4" />
                    {t('projects.index.export_gantt.download', 'Download')}
                </Button>
            ),
        },
        {
            key: 'change-template',
            label: t('projects.show.actions.change_template', 'Change template'),
            icon: <RefreshCw className="h-4 w-4" />,
            onOverflowSelect: openTemplateModal,
            node: (
                <Button type="button" variant="outline" size="sm" className={DETAIL_ACTION_BUTTON_CLASS} onClick={openTemplateModal}>
                    <RefreshCw className="h-4 w-4" />
                    {t('projects.show.actions.change_template', 'Change template')}
                </Button>
            ),
        },
        // Between the template and Edit: changing where a project stands is a
        // deliberate act, and burying it in the details panel's inline selector
        // made it something you stumbled into.
        ...(canChangeStatus
            ? [
                  {
                      key: 'change-status',
                      label: t('projects.show.actions.change_status', 'Change status'),
                      icon: <SignpostBig className="h-4 w-4" />,
                      // Every action here opens a page-level overlay, so the overflow
                      // menu drives the state directly rather than reaching into the
                      // collapsed row to click a button that is no longer visible.
                      onOverflowSelect: openStatusModal,
                      node: (
                          <Button type="button" variant="outline" size="sm" className={DETAIL_ACTION_BUTTON_CLASS} onClick={openStatusModal}>
                              <SignpostBig className="h-4 w-4" />
                              {t('projects.show.actions.change_status', 'Change status')}
                          </Button>
                      ),
                  } satisfies ResponsiveActionItem,
              ]
            : []),
        // Edit → Delete close the row, in that order, on every detail page.
        ...(canUpdateProject
            ? [
                  {
                      key: 'edit',
                      label: t('common.edit', 'Edit'),
                      icon: <Edit className="h-4 w-4" />,
                      pinned: true,
                      onOverflowSelect: () => setEditProjectOpen(true),
                      node: (
                          <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className={DETAIL_ACTION_BUTTON_CLASS}
                              onClick={() => setEditProjectOpen(true)}
                          >
                              <Edit className="h-4 w-4" />
                              {t('common.edit', 'Edit')}
                          </Button>
                      ),
                  } satisfies ResponsiveActionItem,
              ]
            : []),
        ...(canDeleteProject
            ? [
                  {
                      key: 'delete',
                      label: t('projects.show.actions.delete', 'Delete Project'),
                      icon: <Trash2 className="h-4 w-4" />,
                      destructive: true,
                      pinned: true,
                      onOverflowSelect: () => setDeleteConfirmOpen(true),
                      node: (
                          <DeleteActionButton
                              label={t('projects.show.actions.delete', 'Delete Project')}
                              onClick={() => setDeleteConfirmOpen(true)}
                          />
                      ),
                  } satisfies ResponsiveActionItem,
              ]
            : []),
    ];

    return (
        <>
            <Head title={`${t('projects.show.title', 'Project')}: ${projectPageHeading}`} />

            <ShowLayout breadcrumbs={breadcrumbs} title={projectPageHeading}>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 w-full flex-1 flex-col">
                    <div className="mb-6 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center lg:gap-4">
                        <div className="min-w-0 lg:flex-[3]">
                            {isTabletOrMobile ? (
                                <div className="-mx-1 overflow-x-auto pb-1">
                                    <div className="flex w-max min-w-full items-stretch gap-2 px-1">
                                        {[...visibleTabs, ...overflowTabs].map((tab) => {
                                            const isActive = activeTab === tab.value;
                                            const countLabel =
                                                tab.value === 'gantt' && project.tasks.length > 0
                                                    ? ` (${project.tasks.length})`
                                                    : tab.value === 'costs' && project.cost_lines.length > 0
                                                      ? ` (${project.cost_lines.length})`
                                                      : '';

                                            return (
                                                <button
                                                    key={tab.value}
                                                    type="button"
                                                    onClick={() => setActiveTab(tab.value)}
                                                    className={cn(
                                                        'h-auto min-w-[120px] rounded-xl border px-4 py-3 text-left transition-colors',
                                                        isActive
                                                            ? 'border-primary/40 bg-primary/10 text-primary'
                                                            : 'border-border bg-background text-foreground',
                                                    )}
                                                >
                                                    <span className="flex flex-col items-start gap-1.5">
                                                        <span className="text-muted-foreground">{tabIcons[tab.value]}</span>
                                                        <span className="text-sm leading-tight font-medium">
                                                            {tab.label}
                                                            {countLabel}
                                                        </span>
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <TabsList
                                    className={cn(
                                        'bg-muted flex h-auto min-w-0 flex-1 flex-nowrap items-center gap-1 rounded-xl px-1 py-1 sm:w-fit sm:flex-initial',
                                    )}
                                >
                                    {visibleTabs.map((tab) => (
                                        <TabsTrigger key={tab.value} value={tab.value} className={cn('rounded-md text-sm')}>
                                            <span className="inline-flex items-center gap-2">
                                                {tabIcons[tab.value]}
                                                {tab.label}
                                                {tab.value === 'gantt' && project.tasks.length > 0 ? ` (${project.tasks.length})` : null}
                                                {tab.value === 'costs' && project.cost_lines.length > 0 ? ` (${project.cost_lines.length})` : null}
                                            </span>
                                        </TabsTrigger>
                                    ))}
                                    {hasOverflow && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <button
                                                    type="button"
                                                    className={cn(
                                                        'focus-visible:ring-ring inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
                                                        overflowTabs.find((x) => x.value === activeTab)
                                                            ? 'bg-secondary text-secondary-foreground'
                                                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                                                    )}
                                                >
                                                    {overflowTabs.find((x) => x.value === activeTab)
                                                        ? overflowTabs.find((x) => x.value === activeTab)?.label
                                                        : t('work_orders.show.tabs.more', '+{count} more').replace(
                                                              '{count}',
                                                              String(overflowTabs.length),
                                                          )}
                                                </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="min-w-[150px]">
                                                {overflowTabs.map((tab) => (
                                                    <DropdownMenuItem
                                                        key={tab.value}
                                                        onClick={() => setActiveTab(tab.value)}
                                                        className={cn(activeTab === tab.value && 'bg-accent font-medium')}
                                                    >
                                                        <span className="inline-flex items-center gap-2">
                                                            {tabIcons[tab.value]}
                                                            {tab.label}
                                                        </span>
                                                    </DropdownMenuItem>
                                                ))}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                </TabsList>
                            )}
                        </div>

                        <div className="hidden min-w-0 lg:flex lg:flex-[2]">
                            <ResponsiveActions className="w-full" items={actionItems} />
                        </div>
                    </div>

                    <TabsContent value="gantt" className="mt-0 flex min-h-0 flex-1 flex-col outline-none data-[state=inactive]:hidden">
                        <div className="mb-3 flex items-center justify-end">
                            <div className="bg-muted flex items-center gap-1 rounded-xl px-1 py-1">
                                <Button
                                    type="button"
                                    variant={taskView === 'gantt' ? 'secondary' : 'ghost'}
                                    size="sm"
                                    className="rounded-md"
                                    onClick={() => setTaskView('gantt')}
                                    title={t('projects.show.kanban.view_gantt', 'Gantt')}
                                >
                                    <ChartGantt className="h-4 w-4" />
                                </Button>
                                <Button
                                    type="button"
                                    variant={taskView === 'kanban' ? 'secondary' : 'ghost'}
                                    size="sm"
                                    className="rounded-md"
                                    onClick={() => setTaskView('kanban')}
                                    title={t('projects.show.kanban.view_kanban', 'Board')}
                                >
                                    <Columns3 className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        {taskView === 'gantt' ? (
                            <div className="h-[min(78vh,calc(100dvh-12rem))] min-h-[28rem] w-full shrink-0">
                                <GanttChart
                                    tasks={ganttTasks}
                                    projectId={project.id}
                                    projectOwnerId={project.project_owner_id}
                                    users={users.map((u) => ({ id: u.id, name: `${u.first_name} ${u.last_name}` }))}
                                    departmentSortActive={sortByDepartment}
                                    onToggleDepartmentSort={handleSortByDepartment}
                                />
                            </div>
                        ) : (
                            <TaskKanbanBoard
                                tasks={project.tasks}
                                projectOwnerId={project.project_owner_id}
                                users={users.map((u) => ({ id: u.id, name: `${u.first_name} ${u.last_name}` }))}
                            />
                        )}
                    </TabsContent>

                    <TabsContent value="details" className="mt-0 space-y-6">
                        <ProjectDetailsPanel
                            project={project}
                            onNavigateToTab={setActiveTab}
                            linkedCalculationRequest={linkedCalculationRequest}
                            calculationRequestOptions={calculationRequestOptions}
                            canLinkCalculationRequest={canLinkCalculationRequest}
                            contactCandidates={contactCandidates}
                            ownerCandidates={ownerCandidates}
                            projectManagerCandidates={projectManagerCandidates}
                            projectEngineerCandidates={projectEngineerCandidates}
                            canAssignProjectStaff={canAssignProjectStaff}
                            canEditPeople={canUpdateProject}
                            canChangeStatus={canChangeStatus}
                        />
                    </TabsContent>

                    {canViewCosts && (
                        <TabsContent value="costs" className="mt-0">
                            <UniversalCostsComponent
                                entity={project as unknown as EntityWithCosts & { id: number }}
                                entityType="project"
                                entityId={Number(project.id)}
                                canManage={canManageCosts}
                            />
                            {project.purchase_orders && project.purchase_orders.length > 0 && (
                                <LinkedPurchaseOrdersSection purchaseOrders={project.purchase_orders} />
                            )}
                        </TabsContent>
                    )}

                    <TabsContent value="history" className="mt-0">
                        <ProjectHistoryTab projectId={String(project.id)} canRevert={canRevertTasks} />
                    </TabsContent>
                </Tabs>
            </ShowLayout>
            {isTabletOrMobile && (
                <div className="bg-background/95 supports-[backdrop-filter]:bg-background/60 safe-area-inset-bottom fixed right-0 bottom-0 left-0 z-50 border-t p-4 shadow-lg backdrop-blur lg:hidden">
                    <div className="grid grid-cols-5 gap-2">
                        {canUpdateProject && (
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-11 w-full rounded-xl"
                                onClick={() => setEditProjectOpen(true)}
                                aria-label={t('projects.forms.edit_project.button', 'Edit Project')}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>
                        )}
                        {canUpdateManufacturingNumber ? (
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-11 w-full rounded-xl"
                                onClick={() => setManufacturingNumberOpen(true)}
                                aria-label={t('projects.show.manufacturing_number.button', 'Manufacturing number')}
                            >
                                <Hash className="h-4 w-4" />
                            </Button>
                        ) : null}
                        {canManageCosts && (
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-11 w-full rounded-xl"
                                onClick={() => setAddCostOpen(true)}
                                aria-label={t('projects.sidebar.add_cost', 'Add Cost')}
                            >
                                <Plus className="h-4 w-4" />
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-11 w-full rounded-xl"
                            onClick={openExportGanttDialog}
                            aria-label={t('projects.index.export_gantt.download', 'Download')}
                        >
                            <FileSpreadsheet className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-11 w-full rounded-xl"
                            onClick={() => setMobileActionsDrawerOpen(true)}
                            aria-label={t('common.more_actions', 'More actions')}
                        >
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}
            <Drawer open={mobileActionsDrawerOpen} onOpenChange={setMobileActionsDrawerOpen} direction="bottom">
                <DrawerContent className="lg:hidden">
                    <DrawerHeader>
                        <DrawerTitle>{t('projects.show.actions_menu', 'Actions')}</DrawerTitle>
                        <DrawerDescription>{t('common.select_action', 'Select an action')}</DrawerDescription>
                    </DrawerHeader>
                    <div className="space-y-1 p-4 pt-0">
                        {canUpdateProject && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer(() => setEditProjectOpen(true))}
                            >
                                <Edit className="h-4 w-4" />
                                {t('common.edit', 'Edit')}
                            </Button>
                        )}
                        {canUpdateManufacturingNumber && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer(() => setManufacturingNumberOpen(true))}
                            >
                                <Hash className="h-4 w-4" />
                                {t('projects.show.manufacturing_number.button', 'Manufacturing number')}
                            </Button>
                        )}
                        {canManageCosts && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer(() => setAddCostOpen(true))}
                            >
                                <Plus className="h-4 w-4" />
                                {t('projects.sidebar.add_cost', 'Add Cost')}
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant="ghost"
                            className="h-11 w-full justify-start gap-2"
                            onClick={() => requestActionFromDrawer(openExportGanttDialog)}
                        >
                            <FileSpreadsheet className="h-4 w-4" />
                            {t('projects.index.export_gantt.download', 'Download')}
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            className="h-11 w-full justify-start gap-2"
                            onClick={() => requestActionFromDrawer(openTemplateModal)}
                        >
                            <RefreshCw className="h-4 w-4" />
                            {t('projects.show.actions.change_template', 'Change template')}
                        </Button>
                        {canChangeStatus && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer(openStatusModal)}
                            >
                                <SignpostBig className="h-4 w-4" />
                                {t('projects.show.actions.change_status', 'Change status')}
                            </Button>
                        )}
                        {canDeleteProject && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="text-destructive hover:text-destructive h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer(() => setDeleteConfirmOpen(true))}
                            >
                                <Trash2 className="h-4 w-4" />
                                {t('projects.show.actions.delete', 'Delete Project')}
                            </Button>
                        )}
                    </div>
                </DrawerContent>
            </Drawer>
            {isTabletOrMobile && <div className="h-24 lg:h-0" />}

            <EditProjectModal
                project={project}
                users={users.map((u) => ({ id: u.id, name: `${u.first_name} ${u.last_name}` }))}
                open={editProjectOpen}
                onOpenChange={setEditProjectOpen}
            />

            {canUpdateManufacturingNumber && (
                <ProjectManufacturingNumberModal project={project} open={manufacturingNumberOpen} onOpenChange={setManufacturingNumberOpen} />
            )}

            <AddProjectCostModal project={project} open={addCostOpen} onOpenChange={setAddCostOpen} />

            {selectedCost && (
                <EditProjectCostModal
                    cost={selectedCost}
                    isOpen={editModalOpen}
                    onClose={() => {
                        setEditModalOpen(false);
                        setSelectedCost(null);
                    }}
                />
            )}

            {selectedCost && (
                <DeleteProjectCostModal
                    cost={selectedCost}
                    isOpen={deleteModalOpen}
                    onClose={() => {
                        setDeleteModalOpen(false);
                        setSelectedCost(null);
                    }}
                />
            )}

            <ResponsiveModal open={statusModalOpen} onOpenChange={setStatusModalOpen} variant="sheet">
                <ResponsiveModalContent size="md">
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{t('projects.show.change_status.title', 'Change status')}</ResponsiveModalTitle>
                        <ResponsiveModalDescription>
                            {t('projects.show.change_status.description', 'Move the project to another status and say why, if it needs saying.')}
                        </ResponsiveModalDescription>
                    </ResponsiveModalHeader>

                    <ResponsiveModalBody className="space-y-4">
                        <SheetPickerList
                            options={projectStatusOptions}
                            value={selectedStatus}
                            onValueChange={setSelectedStatus}
                            labels={{
                                search: t('projects.show.change_status.search', 'Search statuses…'),
                                empty: t('projects.show.change_status.empty', 'No statuses found.'),
                            }}
                        />

                        <div className="space-y-2">
                            <Label htmlFor="project-status-note">{t('projects.show.change_status.note', 'Note')}</Label>
                            <Textarea
                                id="project-status-note"
                                rows={3}
                                value={statusNote}
                                onChange={(event) => setStatusNote(event.target.value)}
                                placeholder={t('projects.show.change_status.note_placeholder', 'e.g. waiting on the customer drawings')}
                            />
                            <p className="text-muted-foreground text-xs">
                                {t('projects.show.change_status.note_hint', 'Optional. Saved to the project history alongside the change.')}
                            </p>
                        </div>
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={() => setStatusModalOpen(false)}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button type="button" onClick={handleApplyStatus} disabled={!selectedStatus}>
                            {t('projects.show.change_status.apply', 'Save status')}
                        </Button>
                    </ResponsiveModalFooter>
                </ResponsiveModalContent>
            </ResponsiveModal>

            <ResponsiveModal open={templateModalOpen} onOpenChange={setTemplateModalOpen} variant="sheet">
                <ResponsiveModalContent size="md">
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{t('projects.show.change_template.title', 'Change template')}</ResponsiveModalTitle>
                        <ResponsiveModalDescription>
                            {t(
                                'projects.show.change_template.description',
                                'Applying a template will replace the current template-generated tasks for this project.',
                            )}
                        </ResponsiveModalDescription>
                    </ResponsiveModalHeader>

                    <ResponsiveModalBody>
                        {/* The sheet has room to show every template outright, so the
                            list replaces the dropdown; it scrolls with the body. */}
                        <SheetPickerList
                            options={templateOptions.map((option) => ({
                                id: option.value,
                                label: option.label,
                                badge:
                                    option.value === appliedTemplateKey ? (
                                        <Badge variant="secondary">{t('projects.show.change_template.applied', 'Applied')}</Badge>
                                    ) : null,
                            }))}
                            value={selectedTemplate}
                            onValueChange={setSelectedTemplate}
                            searchable={templateOptions.length > 8}
                            labels={{
                                search: t('projects.show.change_template.search', 'Search templates…'),
                                empty: t('projects.show.change_template.empty', 'No templates found.'),
                            }}
                        />
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={() => setTemplateModalOpen(false)}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button type="button" onClick={handleApplyTemplate} disabled={!selectedTemplate}>
                            {t('projects.show.change_template.apply', 'Apply template')}
                        </Button>
                    </ResponsiveModalFooter>
                </ResponsiveModalContent>
            </ResponsiveModal>

            <DeleteConfirmAlert
                open={deleteConfirmOpen}
                onOpenChange={setDeleteConfirmOpen}
                title={t('projects.show.delete.confirm_title', 'Delete Project')}
                description={t(
                    'projects.show.delete.confirm_description',
                    'Are you sure you want to delete ":name"? This action cannot be undone and will permanently remove the project along with all its tasks and data.',
                ).replace(':name', projectPageHeading)}
                cancelLabel={t('projects.show.delete.cancel', 'Cancel')}
                confirmLabel={t('projects.show.delete.confirm_button', 'Delete')}
                onConfirm={() => {
                    setDeleteConfirmOpen(false);
                    router.delete(route('projects.destroy', project.id), {
                        onSuccess: () => {
                            toast.success(t('projects.show.delete.success', 'Project deleted.'));
                        },
                        onError: () => {
                            toast.error(t('projects.show.delete.failed', 'Failed to delete project.'));
                        },
                    });
                }}
            />

            <ResponsiveModal open={exportGanttOpen} onOpenChange={setExportGanttOpen} variant="sheet">
                <ResponsiveModalContent size="lg">
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{t('projects.show.export_gantt.dialog_title', 'Export Gantt to Excel')}</ResponsiveModalTitle>
                        <ResponsiveModalDescription>
                            {t(
                                'projects.show.export_gantt.dialog_description',
                                'Uses the same timeline and options as the projects list export (15 days before today through 45 days ahead, A4). This export includes only this project.',
                            )}
                        </ResponsiveModalDescription>
                    </ResponsiveModalHeader>
                    <ResponsiveModalBody className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <span className="text-sm font-medium">{t('projects.index.export_gantt.timeline_year', 'Project year')}</span>
                            <MultiSelect
                                options={exportTimelineYearOptions}
                                selected={exportTimelineYears}
                                onChange={setExportTimelineYears}
                                placeholder={t('projects.index.export_gantt.timeline_year_placeholder', 'Select years…')}
                            />
                            <p className="text-muted-foreground text-xs">
                                {t(
                                    'projects.show.export_gantt.timeline_year_hint',
                                    'Used for consistency with the global export settings (metadata). This export always includes this project.',
                                )}
                            </p>
                        </div>
                        <div className="flex flex-col gap-2">
                            <div className="flex items-start gap-3">
                                <Checkbox
                                    id="project-export-include-all-timeline-rows"
                                    checked={exportIncludeAllTimelineRows}
                                    onCheckedChange={(v) => setExportIncludeAllTimelineRows(v === true)}
                                />
                                <div className="grid gap-1.5 leading-none">
                                    <Label
                                        htmlFor="project-export-include-all-timeline-rows"
                                        className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                    >
                                        {t('projects.show.export_gantt.include_all_rows', 'Include all rows (ignore export window)')}
                                    </Label>
                                    <p className="text-muted-foreground text-xs">
                                        {t(
                                            'projects.show.export_gantt.include_all_rows_hint',
                                            'When checked, every matching project and task is exported. When unchecked, only rows whose dates overlap the spreadsheet timeline (15 days before today through 45 days ahead) are included.',
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <span className="text-sm font-medium">{t('projects.index.export_gantt.task_statuses', 'Task statuses')}</span>
                            <MultiSelect
                                options={taskStatusExportOptions}
                                selected={exportTaskStatuses}
                                onChange={setExportTaskStatuses}
                                placeholder={t('projects.index.export_gantt.task_statuses_placeholder', 'Select task statuses…')}
                            />
                        </div>
                    </ResponsiveModalBody>
                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={() => setExportGanttOpen(false)}>
                            {t('projects.index.export_gantt.cancel', 'Cancel')}
                        </Button>
                        <Button type="button" onClick={handleExportGanttDownload} disabled={exportTaskStatuses.length === 0}>
                            {t('projects.index.export_gantt.download', 'Download')}
                        </Button>
                    </ResponsiveModalFooter>
                </ResponsiveModalContent>
            </ResponsiveModal>
        </>
    );
}
